import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { PoTableColumn, PoTableComponent } from '@po-ui/ng-components';
import { saveAs } from 'file-saver';

import { Period } from 'src/app/menu/smart-review/core/utils/period.util';
import { CostBucket } from '../../interfaces/costs.interface';
import { breakdownCsvFilename, buildBreakdownCsv } from '../../utils/costs-breakdown-csv.util';
import { costSharePercent, formatCost, formatTokens, sumCostStrings } from '../../utils/costs-format.util';
import { isAggregateTailKey, resolveBucketDisplay } from '../../utils/costs-resource.util';

/** Linha já formatada para o po-table (o template não chama função por ciclo). */
interface BreakdownRow {
  /**
   * A fatia crua que gerou a linha. Fica junto da linha, e não num array paralelo,
   * porque a exportação parte de `filteredItems` do po-table — as linhas que sobraram
   * da busca — e precisa dos números com a precisão original a partir delas. Não é
   * `property` de nenhuma coluna, então não é renderizada nem entra na busca.
   */
  raw: CostBucket;
  key: string;
  /** Só na dimensão `resource`: o tipo do review, separado do identificador. */
  resourceType?: string;
  cost: string;
  runs: number;
  /**
   * Só para changeset/issue: quantos arquivos distintos o review tocou. Vazio para
   * comparison/standalone (sempre 1 arquivo) e para a linha agregada — sem ele, `runs`
   * sozinho não distingue "muitos arquivos" de "reprocessou muito".
   */
  sourceCount: string;
  llmCalls: number;
  tokens: string;
}

/**
 * Tabela de uma dimensão do breakdown de custo. Componente único parametrizado pelo
 * rótulo da coluna-chave ("Usuário", "Área", "Review", "Modelo", "Tipo de análise") —
 * as dimensões diferem só nisso.
 */
@Component({
  selector: 'app-costs-breakdown-table',
  templateUrl: './costs-breakdown-table.component.html',
  styleUrls: ['./costs-breakdown-table.component.css']
})
export class CostsBreakdownTableComponent implements OnChanges {
  /** Rótulo da coluna-chave. Define o que a tabela representa. */
  @Input() keyLabel = 'Chave';

  @Input() rows: CostBucket[] = [];

  /** Moeda vinda da resposta, repassada ao formatador. */
  @Input() currency = 'USD';

  /** Altura fixa (com scroll interno) para dimensões longas, como `resource`. */
  @Input() height?: number;

  /**
   * Quantas chaves a dimensão tem de verdade. Quando passa do teto do backend, a
   * última linha é a cauda somada — e a tabela precisa dizer isso, senão parece que
   * o resto simplesmente não existe.
   */
  @Input() totalKeys?: number;

  /**
   * Na dimensão `resource` a chave é `tipo:identificador`. Quebrada em duas colunas,
   * a tabela lê como o grid de reviews — tipo em tag, identificador ao lado — em vez
   * de um texto único que o usuário precisa interpretar.
   */
  @Input() splitResource = false;

  /** Recorte da tela, só para a exportação (primeira linha do CSV e nome do arquivo). */
  @Input() period?: Period;

  /**
   * Frase de "como ler" uma coluna, exibida acima da tabela. Vazia na maioria das
   * dimensões: só a de review tem uma coluna que engana quem a lê pela primeira vez
   * (execuções acima do número de prompts é reprocessamento, não volume).
   *
   * O texto é da dimensão, então quem o escreve é o painel — este componente é o mesmo
   * para as seis.
   */
  @Input() readingNote = '';

  tableRows: BreakdownRow[] = [];

  columns: PoTableColumn[] = [];

  /**
   * A instância do po-table, para a exportação ler `filteredItems` — as linhas que
   * sobraram da busca. Sem isso, exportar com a busca aplicada devolveria a dimensão
   * inteira, e o arquivo não bateria com o que está na tela.
   *
   * `static: false`: o po-table vive sob `*ngIf="hasRows"`, que só resolve depois do
   * primeiro change detection.
   */
  @ViewChild(PoTableComponent) poTable?: PoTableComponent;

  /** True quando a última linha recebida é a cauda somada — ver `isCapped`. */
  private hasAggregateTail = false;

  /**
   * Nota do recorte, quando a cauda foi agrupada. Ver `buildCappedNote` para o texto.
   *
   * Propriedade calculada em `ngOnChanges`, não getter: ela soma o custo de até 200
   * fatias com `BigInt`, e o template lê a cada ciclo de change detection — como getter,
   * pagaria essa soma em todo ciclo. Mesmo motivo de `areaRows`/`disclaimers` no painel.
   */
  cappedNote = '';

  ngOnChanges(): void {
    // Ordenação desligada de propósito: custo e tokens são strings formatadas em pt-BR,
    // e o sort do po-table as compararia como texto. O backend já entrega por custo desc.
    // A coluna-chave TEM que ter largura fixa como as demais. Sem ela o navegador
    // dimensiona a coluna pelas ~24 linhas que o virtual scroll mantém renderizadas —
    // e o conjunto muda a cada rolagem. Medido na dimensão `resource` com 201 linhas:
    // 276px no topo (chaves curtas como `DVARPR-88`) contra 461px no fim, onde entram
    // os `Origem excluída (uuid)`. A tabela ia de 1086px para 1271px numa viewport de
    // 1059, então as colunas dançavam e Chamadas ao LLM/Tokens saíam da tela.
    //
    // As larguras somam 860px porque é o que cabe no modal: `po-modal-xl` é 70% da tela,
    // então numa janela de 1280px sobram ~850px de área útil. Com os 1050px anteriores,
    // Tokens ficava fora da borda direita — o `po-table-container-fixed-inner` tem
    // `overflow-x: auto` e a coluna não se perdia, mas exigia rolar de lado para ler o
    // último número de toda linha. O que não couber vira reticências com tooltip, via
    // p-hide-text-overflow — e o CSV continua levando o valor inteiro.
    const keyColumns: PoTableColumn[] = this.splitResource
      ? [
          { property: 'resourceType', label: 'Tipo', type: 'cellTemplate', width: '110px', sortable: false },
          { property: 'key', label: this.keyLabel, width: '200px', sortable: false }
        ]
      : [{ property: 'key', label: this.keyLabel, width: '200px', sortable: false }];

    this.columns = [
      ...keyColumns,
      { property: 'cost', label: 'Custo', width: '110px', sortable: false },
      // `runs` fica ao lado do custo porque é o que revela reprocessamento — ver
      // `readingNote`, que explica a conta.
      //
      // 120px, não 110: em 110 o cabeçalho saía "Execuçõ…". A coluna é `type: 'number'`,
      // então o texto encosta na direita e ainda divide espaço com o espaço do sort.
      { property: 'runs', label: 'Execuções', type: 'number', width: '120px', sortable: false },
      // Fontes ao lado de Execuções: é o que separa "muitos arquivos" de "reprocessou
      // muito", que `runs` sozinho não distingue. Só existe na dimensão `resource`
      // (mesma condição de `keyColumns`) — fora dela não há o que contar.
      // Coluna de texto, não `type: 'number'`: fica vazia para comparison/standalone e
      // para a linha agregada, e um número pré-formatado como string evita depender de
      // como o po-table trataria um valor numérico ausente.
      ...(this.splitResource
        ? [{ property: 'sourceCount', label: 'Fontes', width: '80px', sortable: false } as PoTableColumn]
        : []),
      // "Chamadas LLM", não "Chamadas ao LLM": o rótulo inteiro exigia 190px para não
      // cortar, e esses 60px a mais eram justamente o que empurrava Tokens para fora do
      // modal. O CSV mantém o nome por extenso — lá não há disputa por largura.
      { property: 'llmCalls', label: 'Chamadas LLM', type: 'number', width: '130px', sortable: false },
      { property: 'tokens', label: 'Tokens', width: '110px', sortable: false }
    ];

    const buckets = this.rows ?? [];
    this.hasAggregateTail = buckets.length > 0 && isAggregateTailKey(buckets[buckets.length - 1].key);
    this.cappedNote = this.hasAggregateTail ? this.buildCappedNote(buckets) : '';

    this.tableRows = buckets.map(bucket => {
      // `(sem informação)` e `(outros N)` são chaves válidas — entram como qualquer
      // outra. A resolução do rótulo (e do tipo, na dimensão `resource`) é a mesma que
      // a barra e o CSV usam — ver `resolveBucketDisplay`.
      const display = resolveBucketDisplay(bucket, this.splitResource);

      return {
        raw: bucket,
        key: display.key,
        resourceType: display.type,
        cost: formatCost(bucket.cost, this.currency),
        runs: bucket.runs ?? 0,
        sourceCount: bucket.sourceCount != null ? formatTokens(bucket.sourceCount) : '',
        llmCalls: bucket.llmCalls ?? 0,
        tokens: formatTokens(bucket.tokens)
      };
    });
  }

  get hasRows(): boolean {
    return this.tableRows.length > 0;
  }

  /**
   * True quando o backend agrupou a cauda — a última linha é o `(outros N)`.
   *
   * A checagem é pela própria linha agregada, não por `totalKeys > linhas`: com o teto
   * em 200, uma dimensão de 201 chaves volta como 200 linhas + a agregada = 201 linhas,
   * e a comparação diria "coube tudo" com o `(outros 1)` visível na tela sem explicação.
   *
   * `totalKeys` NÃO entra na condição: a linha agregada já prova que a lista é um
   * recorte, e sem `breakdownKeys` (resposta de uma versão anterior da API) exigi-lo
   * esconderia a nota justamente no caso que ela existe para cobrir — a tabela ficaria
   * com um `(outros 1.234)` sem explicação. Só o "de N chaves" depende dele.
   */
  get isCapped(): boolean {
    return this.hasAggregateTail;
  }

  /**
   * Diz o que ficou de fora — e quanto isso custa.
   *
   * O custo da cauda e a fatia dela no período entram porque o número de chaves sozinho
   * não responde a pergunta que importa: se as 132 que sobraram somam 1% do período, o
   * recorte não muda nenhuma conclusão; se somam 28%, muda. Sem isso o usuário teria que
   * rolar até a última linha e comparar de cabeça com o KPI de custo total.
   *
   * A fatia é sobre a soma das PRÓPRIAS fatias, não sobre o total do resumo: as duas
   * fecham (é o que a cauda agregada garante), e assim a nota não depende de um dado que
   * este componente não recebe.
   */
  private buildCappedNote(buckets: readonly CostBucket[]): string {
    const shown = buckets.length - 1; // a última linha é a cauda agregada
    const total = this.totalKeys != null ? `, de ${formatTokens(this.totalKeys)}` : '';

    const tailCost = buckets[buckets.length - 1].cost;
    const share = costSharePercent(tailCost, sumCostStrings(buckets.map(bucket => bucket.cost)));
    const tail = `${formatCost(tailCost, this.currency)}${share ? `, ou ${share} do recorte` : ''}`;

    return `Mostrando as ${formatTokens(shown)} chaves de maior custo${total}. ` +
      `As demais estão somadas na última linha (${tail}), para o total continuar fechando.`;
  }

  /**
   * As linhas que a exportação leva: as que sobraram da busca do po-table, não a
   * dimensão inteira. O que se exporta é o que se vê — é a mesma regra da exportação
   * de execuções do painel, que carrega os filtros da tela.
   *
   * O fallback é só a AUSÊNCIA do po-table (antes do primeiro render): uma
   * `filteredItems` vazia é resposta legítima — a busca não achou nada —, e cair para
   * `tableRows` nesse caso exportaria a dimensão inteira com "Sem dados" na tela.
   */
  private get exportRows(): BreakdownRow[] {
    return this.poTable ? (this.poTable.filteredItems as BreakdownRow[]) : this.tableRows;
  }

  /** True quando a busca deixou de fora alguma linha — o CSV registra isso. */
  private get isSearchFiltered(): boolean {
    return this.exportRows.length < this.tableRows.length;
  }

  /** Nada a exportar quando a dimensão está vazia. A busca sem resultado cai no mesmo caso. */
  get canExport(): boolean {
    return this.exportRows.length > 0;
  }

  /**
   * Baixa a tabela em CSV. Síncrono de propósito: as linhas já estão na memória (o
   * breakdown veio com o resumo), então não há requisição, estado de "exportando" nem
   * como falhar no meio — ao contrário da exportação de execuções, que busca no
   * backend.
   */
  exportCsv(): void {
    const rows = this.exportRows;
    if (!rows.length) { return; }

    const csv = buildBreakdownCsv(rows.map(row => row.raw), {
      keyLabel: this.keyLabel,
      currency: this.currency,
      splitResource: this.splitResource,
      period: this.period,
      totalKeys: this.totalKeys,
      filtered: this.isSearchFiltered
    });

    // O BOM (dentro de `buildBreakdownCsv`) é obrigatório: sem ele o Excel pt-BR lê o
    // arquivo como latin-1 e os acentos viram "Ã§". O charset no type não substitui.
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), breakdownCsvFilename(this.keyLabel, this.period));
  }
}
